import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ExternalLink } from 'lucide-react';

/**
 * AgentEcosystemBar: a compact strip that advertises Kojiru's presence
 * across the agent-tooling ecosystem: MCP Registry (Claude Desktop /
 * Cursor / Windsurf), Coinbase AgentKit, and LangChain / LangGraph.
 *
 * Keep purely presentational. All links point at real listings; badges
 * intentionally do not carry live status dots: they represent product
 * surfaces, not runtime health (that's the /status page).
 */
const surfaces = [
  {
    label: 'MCP Registry',
    sublabel: 'Claude · Cursor · Windsurf',
    href: 'https://registry.modelcontextprotocol.io/servers/io.github.faulknerwayne73-droid%2Fkojiru-mcp',
    testId: 'ecosystem-mcp-registry',
  },
  {
    label: 'Coinbase AgentKit',
    sublabel: '@kojiru/agentkit',
    href: 'https://www.npmjs.com/package/@kojiru/agentkit',
    testId: 'ecosystem-agentkit',
  },
  {
    label: 'LangChain',
    sublabel: '@kojiru/langchain',
    href: 'https://www.npmjs.com/package/@kojiru/langchain',
    testId: 'ecosystem-langchain',
  },
];

export default function AgentEcosystemBar() {
  return (
    <section
      data-testid="agent-ecosystem-bar"
      className="py-14 border-y border-white/5 bg-white/[0.02]"
      aria-labelledby="ecosystem-heading"
    >
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center text-center"
        >
          <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.2em] text-muted-foreground">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span id="ecosystem-heading">
              Available natively in your agent stack
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-4xl">
            {surfaces.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={s.testId}
                className="group flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-black/40 px-5 py-4 hover:border-primary/40 hover:bg-primary/[0.04] transition-colors"
              >
                <div className="text-left">
                  <div className="text-sm font-semibold text-white">
                    {s.label}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {s.sublabel}
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
              </a>
            ))}
          </div>

          <p className="mt-6 text-xs text-muted-foreground max-w-2xl">
            Kojiru&apos;s six credit primitives ship as MCP tools, a Coinbase AgentKit
            action-provider, and a LangChain toolset. One API key, three surfaces,
            zero glue code.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
